import { Report } from "@/services/backend/htz/report/types/report";
import { isImmutable } from "@/services/backend/documents/lib/document-state";
import { createContext, ReactNode, useContext, useMemo } from "react";
import {
  Controls,
  getNodesBounds,
  getViewportForBounds,
  NodeProps,
  Panel,
  ReactFlow,
  useReactFlow,
} from "@xyflow/react";
import { toBlob } from "html-to-image";
import { v4 } from "uuid";
import { DocumentBlobKind } from "@bitory-gmbh/bsm";
import { GSMDocumentKind } from "@/services/backend/documents/types/kind";
import {
  StoreRequest,
  useDocumentBlobDeleteMutation,
  useDocumentBlobStoreMutation,
} from "@/services/backend/filestorage/api/document-blob-api";
import { useHtzReportPatchMutation } from "@/services/backend/htz/report/api/report-api";
import { Button } from "@/shared/components/ui/button";
import t from "@/lang/lang";
import { useToast } from "@/shared/hooks/use-toast";
import { parseRTKQueryError } from "@/shared/components/errors/parse-r-t-k-query-error";
import { useFieldErrors } from "@/shared/lib/errors/err-validation-failed-context";
import {
  AirHandlingUnitData,
  ComponentData,
} from "@/services/backend/htz/ahu/types/air-handling-unit-data";
import { cn } from "@/shared/lib/utils";
import { AhuComponentCatalog } from "@/services/backend/htz/ahu/types/ahu-component-catalog";
import { useHtzAirHandlingUnitDataShowQuery } from "@/services/backend/htz/ahu/api/ahu-data-api";
import {
  useHtzAhuComponentCatalogShowActiveCatalogQuery,
  useHtzAhuComponentCatalogShowQuery,
} from "@/services/backend/htz/ahu/api/ahu-component-catalog-api";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { Result } from "@/services/backend/htz/inspection/components/inspection-result";

export function ReportAhuSchema({ report }: { report: Report }) {
  const {
    data: ahu,
    isLoading: ahuIsLoading,
    error: ahuError,
  } = useHtzAirHandlingUnitDataShowQuery({
    id: report.ahuDataId ?? "",
  });

  const {
    data: activeCatalog,
    isLoading: activeCatalogIsLoading,
    error: activeCatalogError,
  } = useHtzAhuComponentCatalogShowActiveCatalogQuery(
    { companyId: report.companyId },
    { skip: !report.companyId },
  );

  const {
    data: ahuComponentCatalog,
    isLoading: ahuComponentCatalogIsLoading,
    error: ahuComponentCatalogError,
  } = useHtzAhuComponentCatalogShowQuery(
    {
      id: activeCatalog?.catalogId ?? "",
    },
    {
      skip: activeCatalog === undefined || activeCatalog?.catalogId === null,
    },
  );

  if (ahuIsLoading || ahuComponentCatalogIsLoading || activeCatalogIsLoading) {
    return null;
  }

  const error = ahuError || ahuComponentCatalogError || activeCatalogError;
  if (error) {
    return <RTKQueryErrorAlert error={error} />;
  }

  return (
    <ReportAhuContextProvider
      ahu={ahu!}
      ahuComponentCatalog={ahuComponentCatalog!}
      report={report}
    >
      <div className="h-[300px]">
        <ReportModeSchema />
      </div>
    </ReportAhuContextProvider>
  );
}

function ReportAhuContextProvider({
  ahu,
  ahuComponentCatalog,
  report,
  children,
}: {
  ahu: AirHandlingUnitData;
  ahuComponentCatalog: AhuComponentCatalog;
  report: Report;
  children: ReactNode;
}) {
  const value = useMemo(
    () => ({ ahu, ahuComponentCatalog, report }),
    [ahu, ahuComponentCatalog, report],
  );

  return (
    <ReportAhuContext.Provider value={value}>
      {children}
    </ReportAhuContext.Provider>
  );
}

interface ContextInterface {
  ahu: AirHandlingUnitData;
  ahuComponentCatalog: AhuComponentCatalog;
  report: Report;
}

const ReportAhuContext = createContext<ContextInterface>({
  ahu: {} as AirHandlingUnitData,
  ahuComponentCatalog: {} as AhuComponentCatalog,
  report: {} as Report,
});

const gridGap = 12;

function ReportModeSchema() {
  const { ahu, ahuComponentCatalog } = useContext(ReportAhuContext);
  const { components: nodes } = ahu;

  const nodeTypes = useMemo(
    () =>
      ahuComponentCatalog.items.reduce(
        (acc, template) => ({
          ...acc,
          // btw, TS is crazy...
          [template.type]: ReportModeSchemaComponent,
        }),
        {},
      ),
    [ahuComponentCatalog],
  );

  return (
    <div className="h-full rounded-lg border">
      <ReactFlow
        nodes={nodes}
        nodeTypes={nodeTypes}
        nodesDraggable={false}
        snapGrid={[gridGap, gridGap]}
        fitView
        fitViewOptions={{
          padding: 0.25,
        }}
      >
        <Controls />
        <AddSchemaToReportButton />
      </ReactFlow>
    </div>
  );
}

function ReportModeSchemaComponent({ id, data }: NodeProps<ComponentData>) {
  const { ahu, report } = useContext(ReportAhuContext);

  const component = ahu.components.find((c) => c.id === id);
  const item = report.items.find(
    (i) => i.componentId === component?.componentId,
  );

  // Components (the imgs) are expected to be defined in a
  // fixed aspect ratio and same size.
  // The sizeFactor is used to scale them as configured.
  const width = 12 * data.sizeFactor;

  return (
    <div className="relative">
      <img
        src={`data:image/svg+xml;base64,${data.img}`}
        alt={data.name}
        className={cn(
          `w-${width} bg-white`,
          item?.result === Result.WARNING ? "bg-yellow-300" : "",
          item?.result === Result.CRITICAL ? "bg-red-500" : "",
        )}
      />
      <div className="absolute top-12 left-2 text-xs font-bold tracking-tight">
        {data.details}
      </div>
    </div>
  );
}

const imageWidth = 2048;
const imageHeight = 1024;

// AddSchemaToReportButton renders the current AHU schema to a PNG and stores it
// in the blob store at the exact location the report PDF reads it from
// (subdomain "htz", document kind "report", the report id, kind "attachment",
// filename = the report's AhuImageFileID). On first use it also patches the
// report's ahuImageFileID so the PDF and readiness check can find the image.
function AddSchemaToReportButton() {
  const { getNodes } = useReactFlow();
  const { report } = useContext(ReportAhuContext);

  const [store, { isLoading: isStoring }] = useDocumentBlobStoreMutation();
  const [deleteBlob] = useDocumentBlobDeleteMutation();
  const [patch] = useHtzReportPatchMutation();

  const { toast } = useToast();
  // The report is not ready without the image of the schema.
  const { invalid, reset: resetAhuImageFileIdError } =
    useFieldErrors("ahuImageFileId");

  const onClick = () => {
    if (isStoring) {
      return;
    }

    // Compute a transform so all nodes fit into the fixed-size image regardless
    // of the current viewport, then override the viewport transform via the
    // html-to-image style option.
    const nodesBounds = getNodesBounds(getNodes());
    const viewport = getViewportForBounds(
      nodesBounds,
      imageWidth,
      imageHeight,
      1.0,
      1.0,
      0,
    );

    // Reuse the existing file id so the blob is overwritten; only mint a new one
    // on the first upload.
    const fileId = report.ahuImageFileId || v4();

    // @ts-expect-error it is a little unclear what to cast to here, but this
    // will not be null, undefined or anything else causing a runtime error
    toBlob(document.querySelector(".react-flow__viewport"), {
      backgroundColor: "#ffffff",
      width: imageWidth,
      height: imageHeight,
      style: {
        width: imageWidth,
        height: imageHeight,
        transform: `translate(${viewport.x}px, ${viewport.y}px) scale(${viewport.zoom})`,
      },
    }).then((blob) => {
      if (!blob) {
        toast({
          title: t("Fehler"),
          description: t("Das Schema konnte nicht gerendert werden."),
          variant: "destructive",
        });
        return;
      }

      const request: StoreRequest = {
        subdomain: "htz",
        documentKind: GSMDocumentKind.Report,
        documentId: report.id,
        version: "",
        kind: DocumentBlobKind.Attachment,
        filename: fileId,
        mediaType: "image/png",
        data: new File([blob], `${fileId}.png`, { type: "image/png" }),
      };

      store(request)
        .unwrap()
        .then(() =>
          report.ahuImageFileId !== fileId
            ? patch({ id: report.id, ahuImageFileId: fileId })
                .unwrap()
                .catch((err) => {
                  // The reference was never taken, so the freshly minted
                  // file is orphaned - delete it best effort. Retrying
                  // mints another id, so nothing would clean it up later.
                  deleteBlob({
                    subdomain: "htz",
                    documentKind: GSMDocumentKind.Report,
                    documentId: report.id,
                    version: "",
                    kind: DocumentBlobKind.Attachment,
                    filename: fileId,
                  })
                    .unwrap()
                    .catch(() => {});
                  throw err;
                })
            : undefined,
        )
        .then(() => {
          resetAhuImageFileIdError();
          toast({ title: t("Schema zum Bericht hinzugefügt") });
        })
        .catch((err) => {
          toast({ ...parseRTKQueryError(err), variant: "destructive" });
        });
    });
  };

  return (
    <Panel position="top-right">
      <Button
        onClick={onClick}
        disabled={isStoring || isImmutable(report)}
        aria-invalid={invalid || undefined}
      >
        {t("Zum Bericht hinzufügen")}
      </Button>
    </Panel>
  );
}
